import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #pendingUpdates = new Set();
  #syncController = null;
  #syncVersion = 0;
  #lastSyncedAt = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#trackCartUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#trackCartUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#trackCartUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#trackCartUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#invalidateSync();
    this.#pendingUpdates.clear();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    void this.#syncCart();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    this.#trackCartUpdate(event);
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );
    const earlySections = /** @type {any} */ (event).dwilSectionsReady;

    // New product-form adds open with their returned contents, rather than flashing the old cart.
    if (shouldAutoOpen && !sourceModal && !earlySections && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    const displayReady = earlySections
      ? earlySections.then((result) => result?.detail?.sections?.[sectionId] ? result : event.promise)
      : event.promise;
    displayReady
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #invalidateSync() {
    this.#syncVersion += 1;
    this.#syncController?.abort();
    this.#syncController = null;
    this.#lastSyncedAt = 0;
  }

  #trackCartUpdate = (event) => {
    this.#invalidateSync();
    const promise = event.promise;
    if (!promise || this.#pendingUpdates.has(promise)) return;
    this.#pendingUpdates.add(promise);
    const version = this.#syncVersion;
    const settle = (result) => {
      if (!this.#pendingUpdates.delete(promise) || !this.isConnected) return;
      const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
      if (version === this.#syncVersion && result?.detail?.sections?.[sectionId]) {
        this.#lastSyncedAt = Date.now();
      }
      if (!this.#pendingUpdates.size) void this.#syncCart();
    };
    promise.then(settle, () => settle(null));
  };

  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.#invalidateSync();
    void this.#syncCart();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    this.#invalidateSync();
    void this.#syncCart();
  };

  async #syncCart() {
    if (!this.isConnected || !this.#themeDrawer?.isOpen || this.#pendingUpdates.size || this.#syncController) return;
    if (Date.now() - this.#lastSyncedAt < 1000) return;
    const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (!sectionId) return;
    const controller = new AbortController();
    const version = this.#syncVersion;
    this.#syncController = controller;
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
      const html = await response.text();
      if (controller.signal.aborted || version !== this.#syncVersion || !this.isConnected || this.#pendingUpdates.size) return;
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      if (!fragment.getElementById(`shopify-section-${sectionId}`)?.querySelector('[data-hydration-key="cart-drawer-inner"]')) {
        throw new Error('Cart refresh returned no drawer content');
      }
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      if (version === this.#syncVersion) this.#lastSyncedAt = Date.now();
      this.#updateStickyState();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer] Refresh unavailable:', error);
    } finally {
      if (this.#syncController === controller) this.#syncController = null;
    }
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
